import React, { useState, useEffect, useRef } from 'react';
import { Modal, ScrollView, View, TouchableOpacity, useWindowDimensions, Text } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  arrayUnion,
  arrayRemove,
  updateDoc,
  deleteField,
  collectionGroup
} from "firebase/firestore";
import TextInput from "../../../components/TextInput";
import {
  StandardText,
  FooterText,
  SmallText,
  SubTitle
} from "../../../config/theme/globalStyles";
import Button from "../../../components/Button";
import AdminButton from "../../../components/AdminButton";
import { ViewTextInput, ViewButton, ViewRow, ModalBackground, ModalContainer, Header, CloseButton, Content } from "./style";
import { getProductById } from '../../../services/guru';
import {
  previewProductExpirationMigration,
  applyProductExpirationMigration,
} from './service/product-service';
import { getHotmartDelayedSubscriptions, cancelHotmartSubscriptions } from '../../Users/user-service';
import { firestore } from '../../../services/firebase';
import GenericProduct from "./GenericProduct";
import { Octicons } from '@expo/vector-icons'
import { CheckBox } from "react-native-elements";
import { updateRenewalOfferBatch } from "../../../services/guru/updateRenewalOfferBatch";
import { TextInputMask } from "react-native-masked-text";
import { fixedExpirationDateValidator } from "../../../utils/validators";
import {
  FORM,
  Field,
  FieldLabel,
  FieldError,
  Hint,
  SectionCard,
  SegmentedControl,
  InfoBanner,
  Badge,
} from "./formStyles";
import AdminInput from "../../../components/AdminInput";
import AdminSwitch from "../../../components/AdminSwitch";
import AdminAlert from "../../../components/AdminAlert";
import AdminDatePicker from "../../../components/AdminDatePicker";
import AdminCheckbox from "../../../components/AdminCheckbox";
import { CatalogVitrineFields } from "../../../components/CatalogVitrineFields";
import ProductCatalogFilters from "../../../components/ProductCatalogFilters";
import { ADMIN_FONT } from "../../../config/theme/adminColors";

const isProductOrEticket = (type) => {
  return type === 'product' || type === 'eticket';
};

const isHotmartMarketplace = (marketplaceName) =>
  String(marketplaceName || '').trim().toLowerCase() === 'hotmart';

const getProductAccessId = (product) => product?.productId || product?.id;

const sortHotmartDelayedSubscriptions = (items) =>
  [...items].sort((a, b) => {
    if (b.daysDelayed !== a.daysDelayed) {
      return b.daysDelayed - a.daysDelayed;
    }

    return (a.email || '').localeCompare(b.email || '', 'pt-BR');
  });

const getHotmartFeedbackVariant = (message) => {
  if (!message) return 'info';
  if (message.startsWith('Erro')) return 'error';
  if (message.includes('cancelada')) return 'success';
  return 'info';
};

const getHotmartSubscriptionSelectionId = (subscription) =>
  subscription?.id ||
  subscription?.subscriberCode ||
  subscription?.subscriber_code ||
  String(subscription?.subscriptionId || subscription?.subscription_id || '');

const normalizeHotmartDelayedSubscription = (subscription) => {
  const lastRecurrencyRequestDate =
    subscription?.lastRecurrencyRequestDate ||
    subscription?.last_recurrency?.request_date ||
    subscription?.date_next_charge ||
    null;

  let daysDelayed = subscription?.daysDelayed;

  if (daysDelayed == null && lastRecurrencyRequestDate) {
    const timestamp =
      lastRecurrencyRequestDate > 10000000000
        ? lastRecurrencyRequestDate
        : lastRecurrencyRequestDate * 1000;
    daysDelayed = Math.floor((Date.now() - timestamp) / (24 * 60 * 60 * 1000));
  }

  const subscriberCode =
    subscription?.subscriberCode ||
    subscription?.subscriber_code ||
    '';

  const subscriptionId =
    subscription?.subscriptionId ||
    subscription?.subscription_id ||
    null;

  return {
    id: subscriberCode || String(subscriptionId || ''),
    subscriberCode,
    subscriptionId,
    email:
      subscription?.email ||
      subscription?.subscriber?.email ||
      subscription?.buyer?.email ||
      '',
    name:
      subscription?.name ||
      subscription?.subscriber?.name ||
      subscription?.buyer?.name ||
      '',
    daysDelayed: Number.isFinite(Number(daysDelayed)) ? Number(daysDelayed) : 0,
    status: subscription?.status || 'DELAYED',
    lastRecurrencyRequestDate,
  };
};

// As datas de acesso são instantes ABSOLUTOS (fim do dia em BRT). Formatar no
// fuso do navegador mostraria o dia seguinte para quem acessa o painel fora do
// Brasil — o dia exibido tem que ser sempre o dia brasileiro.
const formatAccessDate = (epochMs) =>
  epochMs == null
    ? 'sem data de fim'
    : new Date(epochMs).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });

// Como a mudança aparece na linha do aluno. `tone` decide a cor: perder acesso
// e perder o vitalício precisam saltar aos olhos antes de o admin confirmar.
const describeGrantChange = (grant, targetDate) => {
  const target = formatAccessDate(targetDate);

  if (grant.change === 'limit') {
    return { text: `Sem data de fim → ${target}`, tone: 'warn' };
  }

  const current = formatAccessDate(grant.currentExpiration);
  const days = Math.abs(grant.deltaDays ?? 0);
  const suffix = days === 1 ? '1 dia' : `${days} dias`;

  if (grant.change === 'shorten') {
    return { text: `${current} → ${target} (−${suffix})`, tone: 'danger' };
  }
  return { text: `${current} → ${target} (+${suffix})`, tone: 'neutral' };
};

const GRANT_STATUS_BADGE = {
  active: null, // o normal não precisa de selo
  expired: { label: 'Expirado', variant: 'danger' },
  perpetual: { label: 'Vitalício', variant: 'info' },
};

// Erros de callable chegam como 'functions/internal' quando a função não existe
// no projeto (o 404 do Cloud Functions não devolve cabeçalho CORS, e o SDK
// traduz o bloqueio do navegador para 'internal'). Sem esta tradução o admin lê
// só "internal" na tela e não tem o que fazer com isso.
const describeMigrationError = (error, fallback) => {
  const code = String(error?.code || '');

  if (code.includes('internal') || code.includes('unavailable')) {
    return 'Não foi possível falar com o servidor. Se as funções do projeto ainda não foram publicadas, publique-as e tente novamente.';
  }
  if (code.includes('unauthenticated') || code.includes('permission-denied')) {
    return 'Seu usuário não tem permissão para esta ação.';
  }

  return error?.message || fallback;
};

const EditProductModal = ({ isVisible, onClose, onEdit, onDelete, isDeleting, selectedProduct }) => {
  const vitrineRef = useRef(null);
  const [vitrineFeedback, setVitrineFeedback] = useState(null);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const [productInfo, setProductInfo] = useState('');
  const [offersInfos, setOffersInfos] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [productType, setProductType] = useState("");
  const [archived, setArchived] = useState(true);
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [description, setDescription] = useState('');

  const [hasExpirationDate, setHasExpirationDate] = useState(false);
  const [expirationDays, setExpirationDays] = useState(0);
  const [showExpirationDaysWarning, setShowExpirationDaysWarning] = useState(false);
  const [guruErrorMessage, setGuruErrorMessage] = useState('');

  const [expirationType, setExpirationType] = useState("days");
  const [fixedExpirationDate, setFixedExpirationDate] = useState("");
  const [fixedExpirationDateError, setFixedExpirationDateError] = useState("");

  // Aplicação RETROATIVA da data fixa aos alunos que já têm o produto.
  // `saved*` é o que está no Firestore (não o que está no formulário): a
  // migração só pode rodar sobre uma configuração já salva, porque quem calcula
  // a data alvo é o backend, lendo o produto.
  const [savedExpirationType, setSavedExpirationType] = useState("days");
  const [savedFixedExpirationDate, setSavedFixedExpirationDate] = useState("");
  const [migrationUsers, setMigrationUsers] = useState(null); // null = ainda não consultado
  const [migrationSelectedIds, setMigrationSelectedIds] = useState([]);
  const [migrationTargetDate, setMigrationTargetDate] = useState(null);
  const [migrationSummary, setMigrationSummary] = useState(null);
  const [isLoadingMigration, setIsLoadingMigration] = useState(false);
  const [isApplyingMigration, setIsApplyingMigration] = useState(false);
  const [migrationFeedback, setMigrationFeedback] = useState("");
  const [migrationError, setMigrationError] = useState("");

  //Change renewal offer (Guru)
  const { height: windowHeight } = useWindowDimensions();
  const [newRenewalOfferId, setNewRenewalOfferId] = useState("");
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedSubscriptionIds, setSelectedSubscriptionIds] = useState([]);
  const [isRenewalSectionOpen, setIsRenewalSectionOpen] = useState(false);
  const [isUpdatingRenewalOffer, setIsUpdatingRenewalOffer] = useState(false);
  const [renewalFeedbackMessage, setRenewalFeedbackMessage] = useState("");
  const [renewalFailedEmails, setRenewalFailedEmails] = useState([]);
  const [selectedOfferForRenewalId, setSelectedOfferForRenewalId] = useState(null);
  const [isLoadingHotmartDelayedSubscriptions, setIsLoadingHotmartDelayedSubscriptions] = useState(false);
  const [hotmartDelayedSubscriptions, setHotmartDelayedSubscriptions] = useState([]);
  const [selectedHotmartSubscriptionCodes, setSelectedHotmartSubscriptionCodes] = useState([]);
  const [hotmartDelayedFeedbackMessage, setHotmartDelayedFeedbackMessage] = useState('');
  const [isCancellingHotmartSubscriptions, setIsCancellingHotmartSubscriptions] = useState(false);

  const [expandedCategoryIds, setExpandedCategoryIds] = useState([]);
  const [tree, setTree] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [loadingInitialLinks, setLoadingInitialLinks] = useState(true);

  useEffect(() => {
    (async () => {
      setLoadingTree(true);
      // fetch categories+cards tree, já ordenando
      try {
        const catsSnap = await getDocs(
          query(collection(firestore, "categories"), orderBy("order", "asc"))
        );
        
        const tmp = [];
        
        for (let catDoc of catsSnap.docs) {
          const cat = {
            id: catDoc.id,
            title: catDoc.data().title,
            selected: false,
            cards: []
          };
        
          const cardsSnap = await getDocs(
            query(collection(firestore, "categories", cat.id, "cards"), orderBy("order", "asc"))
          );
        
          cardsSnap.docs.forEach(cardDoc => {
            const data = cardDoc.data();
        
            cat.cards.push({
              id: cardDoc.id,
              title: data.title,
              productIds: data.productIds || [],
              selected: false
            });
          });
        
          tmp.push(cat);
        }
        setTree(tmp);
        setLoadingTree(false)
      } catch (err) {
        console.error("Erro ao carregar categorias:", err);
      }
    })();
  }, []);

  useEffect(() => {
    const productId = getProductAccessId(selectedProduct);
    if (!productId) return;
    if (!tree.length) return;
    if (!loadingInitialLinks) return;
  
    const updated = tree.map(cat => {
      const hasAny = cat.cards.some(cd => cd.productIds?.includes(productId));
  
      return {
        ...cat,
        selected: hasAny,
        cards: cat.cards.map(cd => ({
          ...cd,
          selected: cd.productIds?.includes(productId)
        }))
      };
    });
  
    setTree(updated);
    setLoadingInitialLinks(false);
  }, [tree, selectedProduct]);

  useEffect(() => {
    if (!isVisible || selectedProduct?.marketplace !== "Guru Digital") {
      return;
    }

    setLoadingInitialLinks(true);
    setHotmartDelayedSubscriptions([]);
    setSelectedHotmartSubscriptionCodes([]);
    setHotmartDelayedFeedbackMessage('');
    setGuruErrorMessage('');

    const initModal = async () => {
      setLoadingSearch(true);

      try {
        await loadProductDataFromFirestore();
      } catch (err) {
        console.error('Erro ao carregar produto do Firestore:', err);
        setProductType(selectedProduct?.type || '');
      } finally {
        setLoadingSearch(false);
      }

      try {
        const infos = await getProductById(selectedProduct.productId);
        setProductInfo(infos.product);
        setOffersInfos(infos.offers);
        setGuruErrorMessage('');

        if (infos.product?.type) {
          setProductType(infos.product.type);
          setHasExpirationDate(isProductOrEticket(infos.product.type));
        }
      } catch (error) {
        console.warn('Não foi possível buscar produto na Guru. Usando dados locais.', error);
        setGuruErrorMessage(
          'ID Informado não foi encontrado nos produtos existentes na Guru.'
        );
        await loadOffersFromFirestore(selectedProduct.id);
      }
    };

    initModal();
  }, [isVisible, selectedProduct?.id]);

  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  const handleToggleRenewalSection = (offerId) => {
    setSelectedOfferForRenewalId(offerId);
    setIsRenewalSectionOpen((prev) => (offerId ? true : !prev));
  };

  const handleLoadSubscriptionsForOffer = async (offerId) => {
    if (!selectedProduct?.productId) return;
    if (!offerId) return;

    try {
      setLoadingSubscriptions(true);
      setRenewalFeedbackMessage("");
      setRenewalFailedEmails([]);

      const subsGroupRef = collectionGroup(firestore, "subscriptions");
      const snap = await getDocs(subsGroupRef);

      const allSubs = snap.docs.map((docSnap) => {
        const data = docSnap.data();
        const parentUserRef = docSnap.ref.parent?.parent;

        return {
          id: docSnap.id,
          userId: parentUserRef?.id || "",
          status: data?.status,
          originalGuruStatus: data?.original_guru_status,
          productId: data?.items?.[0]?.plan?.product,
          currentOfferId: data?.items?.[0]?.price?.id,
        };
      });

      const filtered = allSubs.filter((sub) => {
        const sameProduct =
          String(sub.productId) === String(selectedProduct.productId);
        const sameOffer =
          String(sub.currentOfferId) === String(offerId);

        const normalizedStatus = String(sub.status || "").toLowerCase();
        const normalizedGuruStatus = String(
          sub.originalGuruStatus || ""
        ).toLowerCase();

        const isCanceled =
          normalizedStatus.includes("cancel") ||
          normalizedGuruStatus.includes("cancel");

        return sameProduct && sameOffer && !isCanceled;
      });

      // Buscar e-mail dos usuários para exibir no lugar do ID
      const uniqueUserIds = Array.from(
        new Set(filtered.map((s) => s.userId).filter(Boolean))
      );
      const userEmailMap = {};

      await Promise.all(
        uniqueUserIds.map(async (uid) => {
          try {
            const userSnap = await getDoc(doc(firestore, "users", uid));
            if (userSnap.exists()) {
              const uData = userSnap.data();
              userEmailMap[uid] = uData?.Email || uData?.email || "";
            }
          } catch (e) {
            console.warn("Erro ao buscar usuário para email:", uid, e);
          }
        })
      );

      const enriched = filtered.map((sub) => ({
        ...sub,
        userEmail: userEmailMap[sub.userId] || "",
      }));

      // Ordenar alfabeticamente pelo e-mail (fallback para userId)
      enriched.sort((a, b) => {
        const aKey = (a.userEmail || a.userId || "").toLowerCase();
        const bKey = (b.userEmail || b.userId || "").toLowerCase();
        if (aKey < bKey) return -1;
        if (aKey > bKey) return 1;
        return 0;
      });

      setSubscriptions(enriched);
      setSelectedSubscriptionIds(enriched.map((s) => s.id));

      if (filtered.length === 0) {
        setRenewalFeedbackMessage(
          "Nenhuma assinatura espelhada no Firestore encontrada para este produto."
        );
      } else {
        setRenewalFeedbackMessage(
          `Encontradas ${filtered.length} assinaturas para este produto.`
        );
      }
    } catch (err) {
      console.error("Erro ao carregar assinaturas para produto:", err);
      setRenewalFeedbackMessage(
        "Erro ao carregar assinaturas. Verifique o console para mais detalhes."
      );
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  // Só lista quem está atrasado há HOTMART_MIN_DELAYED_DAYS dias ou mais,
  // contados da data de vencimento — um valor de 0 aqui já causou o
  // cancelamento indevido de assinantes com 1-2 dias de atraso que ainda
  // tinham chance de pagar (o backend agora rejeita valores negativos, mas o
  // "5 dias de atraso" pedido pelo cliente vem daqui).
  const HOTMART_MIN_DELAYED_DAYS = 5;

  const handleListHotmartDelayedSubscriptions = async () => {
    if (!selectedProduct?.productId) return;

    setIsLoadingHotmartDelayedSubscriptions(true);
    setHotmartDelayedFeedbackMessage('');

    try {
      const response = await getHotmartDelayedSubscriptions({
        productId: selectedProduct.productId,
        delayedDays: HOTMART_MIN_DELAYED_DAYS,
      });
      const result = response?.data;
      const items = sortHotmartDelayedSubscriptions(
        (result?.subscriptions || []).map(normalizeHotmartDelayedSubscription)
      );

      setHotmartDelayedSubscriptions(items);
      setSelectedHotmartSubscriptionCodes(
        items.map(getHotmartSubscriptionSelectionId).filter(Boolean)
      );

      if (items.length === 0) {
        setHotmartDelayedFeedbackMessage('Nenhuma assinatura atrasada há 5 dias ou mais encontrada para este produto.');
      } else {
        setHotmartDelayedFeedbackMessage(
          `${items.length} assinatura(s) atrasada(s) há 5 dias ou mais encontrada(s).`
        );
      }
    } catch (error) {
      console.error('Erro ao listar assinaturas Hotmart atrasadas:', error);
      setHotmartDelayedSubscriptions([]);
      setSelectedHotmartSubscriptionCodes([]);
      setHotmartDelayedFeedbackMessage('Erro ao listar assinaturas atrasadas na Hotmart.');
    } finally {
      setIsLoadingHotmartDelayedSubscriptions(false);
    }
  };

  const handleToggleHotmartSubscriptionSelection = (selectionId) => {
    setSelectedHotmartSubscriptionCodes((prev) => {
      if (prev.includes(selectionId)) {
        return prev.filter((id) => id !== selectionId);
      }
      return [...prev, selectionId];
    });
  };

  const handleToggleSelectAllHotmartSubscriptions = () => {
    const allSelectionIds = hotmartDelayedSubscriptions
      .map(getHotmartSubscriptionSelectionId)
      .filter(Boolean);

    if (
      selectedHotmartSubscriptionCodes.length === allSelectionIds.length &&
      allSelectionIds.length > 0
    ) {
      setSelectedHotmartSubscriptionCodes([]);
      return;
    }

    setSelectedHotmartSubscriptionCodes(allSelectionIds);
  };

  const getSelectedHotmartSubscriberCodes = () =>
    hotmartDelayedSubscriptions
      .filter((subscription) =>
        selectedHotmartSubscriptionCodes.includes(
          getHotmartSubscriptionSelectionId(subscription)
        )
      )
      .map((subscription) => subscription.subscriberCode)
      .filter(Boolean);

  const handleCancelHotmartSubscriptions = async () => {
    const subscriberCodes = getSelectedHotmartSubscriberCodes();

    if (subscriberCodes.length === 0) {
      setHotmartDelayedFeedbackMessage('Selecione ao menos uma assinatura para cancelar.');
      return;
    }

    const confirmed = window.confirm(
      `Cancelar ${subscriberCodes.length} assinatura(s) selecionada(s) na Hotmart? Esta ação não pode ser desfeita.`
    );

    if (!confirmed) {
      return;
    }

    setIsCancellingHotmartSubscriptions(true);
    setHotmartDelayedFeedbackMessage('');

    try {
      const response = await cancelHotmartSubscriptions({
        subscriberCodes,
        sendMail: false,
      });
      const cancelledCount = response?.data?.cancelledCount ?? subscriberCodes.length;
      const cancelledCodes = new Set(response?.data?.subscriberCodes || subscriberCodes);
      const failedCount = response?.data?.failedSubscriberCodes?.length || 0;

      setHotmartDelayedSubscriptions((prev) =>
        prev.filter((subscription) => !cancelledCodes.has(subscription.subscriberCode))
      );
      setSelectedHotmartSubscriptionCodes([]);
      setHotmartDelayedFeedbackMessage(
        failedCount
          ? `${cancelledCount} assinatura(s) cancelada(s); ${failedCount} falharam e permanecem na lista.`
          : `${cancelledCount} assinatura(s) cancelada(s) com sucesso na Hotmart.`
      );
    } catch (error) {
      console.error('Erro ao cancelar assinaturas Hotmart:', error);
      setHotmartDelayedFeedbackMessage('Erro ao cancelar assinaturas na Hotmart.');
    } finally {
      setIsCancellingHotmartSubscriptions(false);
    }
  };

  const handleToggleSubscriptionSelection = (subscriptionId) => {
    setSelectedSubscriptionIds((prev) => {
      if (prev.includes(subscriptionId)) {
        return prev.filter((id) => id !== subscriptionId);
      }
      return [...prev, subscriptionId];
    });
  };

  const handleToggleSelectAllSubscriptions = () => {
    if (selectedSubscriptionIds.length === subscriptions.length) {
      setSelectedSubscriptionIds([]);
    } else {
      setSelectedSubscriptionIds(subscriptions.map((s) => s.id));
    }
  };

  const handleApplyRenewalOfferToSelected = async () => {
    if (!newRenewalOfferId.trim()) {
      setRenewalFeedbackMessage("Informe o ID da nova oferta de renovação.");
      return;
    }

    if (selectedSubscriptionIds.length === 0) {
      setRenewalFeedbackMessage("Selecione ao menos uma assinatura.");
      return;
    }

    try {
      console.log("selectedSubscriptionIds are", selectedSubscriptionIds)
      console.log("newRenewalOfferId is",  newRenewalOfferId.trim())

      setIsUpdatingRenewalOffer(true);
      setRenewalFeedbackMessage("");
      setRenewalFailedEmails([]);

      const result = await updateRenewalOfferBatch(
        selectedSubscriptionIds,
        newRenewalOfferId.trim()
      );

      const updated = result?.updatedCount || 0;
      const failed = result?.failedCount || 0;

      if (failed === 0) {
        setRenewalFailedEmails([]);
        setRenewalFeedbackMessage(
          `Oferta de renovação atualizada com sucesso para ${updated} assinatura(s).`
        );
      } else {
        const failedSubscriptionIds = (result?.errors || [])
          .map((e) => String(e?.subscriptionId || ""))
          .filter(Boolean);

        const failedEmails = Array.from(
          new Set(
            failedSubscriptionIds
              .map(
                (subscriptionId) =>
                  subscriptions.find(
                    (s) => String(s?.id) === String(subscriptionId)
                  )?.userEmail
              )
              .filter(Boolean)
          )
        );

        setRenewalFailedEmails(failedEmails);
        setRenewalFeedbackMessage(
          `Oferta de renovação atualizada para ${updated} assinatura(s). Falha em ${failed} assinatura(s).`
        );
      }
    } catch (err) {
      console.error("Erro ao aplicar oferta de renovação em lote:", err);
      setRenewalFailedEmails([]);
      setRenewalFeedbackMessage(
        "Erro ao aplicar oferta de renovação. Verifique o console para mais detalhes."
      );
    } finally {
      setIsUpdatingRenewalOffer(false);
    }
  };


  const loadOffersFromFirestore = async (productDocId) => {
    if (!productDocId) return;

    const offersSnap = await getDocs(
      collection(firestore, 'products', productDocId, 'offers')
    );
    const offers = offersSnap.docs.map((offerDoc) => offerDoc.data());

    if (offers.length > 0) {
      setOffersInfos({ data: offers });
    }
  };

  const loadProductDataFromFirestore = async () => {
    const productRef = doc(firestore, "products", selectedProduct?.id);
    const productDocSnap = await getDoc(productRef);

    if (productDocSnap.exists()) {
      const productData = productDocSnap.data();
      setDescription(productData?.description || '');
      setArchived(productData?.archived ?? true);
      setCheckoutUrl(productData?.checkoutUrl || '');
      setHasExpirationDate(productData?.hasExpirationDate ?? false);
      setExpirationDays(productData?.days || 0);
      setExpirationType(productData?.expirationType || "days");
      setFixedExpirationDate(productData?.fixedExpirationDate || "");
      setProductType(productData?.type || selectedProduct?.type || '');
      setSavedExpirationType(productData?.expirationType || "days");
      setSavedFixedExpirationDate(productData?.fixedExpirationDate || "");
    } else {
      setProductType(selectedProduct?.type || '');
    }
  };

  // LISTAR é livre (leitura pura, aceita data ainda não salva); APLICAR exige a
  // configuração salva, porque a data gravada nos alunos é sempre a do produto.
  const hasUnsavedValidityChange =
    expirationType !== savedExpirationType ||
    (expirationType === "fixedDate" && fixedExpirationDate !== savedFixedExpirationDate);

  const canApplyMigration =
    savedExpirationType === "fixedDate" &&
    !!savedFixedExpirationDate &&
    !hasUnsavedValidityChange;

  // Evita repetir a varredura de alunos para o mesmo produto+data (a consulta lê
  // a coleção inteira de alunos). Uma nova data escolhida muda a chave e libera.
  const lastPreviewKeyRef = useRef(null);

  // `silent`: recarga após aplicar a migração — não pode limpar nem sobrescrever
  // a mensagem de sucesso que o admin acabou de receber.
  // `force`: ignora o cache da última consulta (botão "Atualizar lista" e recarga
  // pós-gravação, que precisam refletir o estado novo).
  const handleLoadAffectedStudents = async ({ silent = false, force = false } = {}) => {
    const productId = getProductAccessId(selectedProduct);
    if (!productId) return;

    const previewKey = `${productId}|${fixedExpirationDate}`;
    if (!force && lastPreviewKeyRef.current === previewKey) return;
    lastPreviewKeyRef.current = previewKey;

    setIsLoadingMigration(true);
    if (!silent) setMigrationFeedback("");
    setMigrationError("");

    try {
      const result = await previewProductExpirationMigration(productId, fixedExpirationDate);
      const users = result?.users || [];

      setMigrationUsers(users);
      setMigrationTargetDate(result?.targetDate ?? null);
      setMigrationSummary({
        alreadyAlignedCount: result?.alreadyAlignedCount || 0,
        truncated: !!result?.truncated,
      });
      // Pré-seleção vinda do backend: ativos com data divergente entram
      // marcados; expirados e vitalícios ficam de fora até o admin decidir.
      setMigrationSelectedIds(
        users.filter((user) => user.selectedByDefault).map((user) => user.userId)
      );

      if (users.length === 0 && !silent) {
        setMigrationFeedback(
          result?.alreadyAlignedCount > 0
            ? `Nenhum aluno para ajustar: os ${result.alreadyAlignedCount} alunos deste produto já estão na data fixa.`
            : 'Nenhum aluno com data diferente foi encontrado para este produto.'
        );
      }
    } catch (error) {
      console.error('Erro ao listar alunos afetados:', error);
      // Libera a chave para que uma nova tentativa não caia no cache do erro.
      lastPreviewKeyRef.current = null;
      setMigrationUsers(null);
      setMigrationError(
        describeMigrationError(
          error,
          'Não foi possível listar os alunos afetados. Tente novamente.'
        )
      );
    } finally {
      setIsLoadingMigration(false);
    }
  };

  // Escolher a data fixa já mostra o impacto, sem clique extra: é a informação
  // que o admin precisa para decidir se salva. O debounce evita disparar a
  // varredura a cada dia que ele clica enquanto navega pelo calendário.
  useEffect(() => {
    if (!isVisible) return;
    if (selectedProduct?.marketplace !== "Guru Digital") return;
    if (expirationType !== "fixedDate") return;
    // Só consulta com data completa e válida (o validador exige data futura).
    if (fixedExpirationDateValidator(fixedExpirationDate) !== "") return;

    const timer = setTimeout(() => { handleLoadAffectedStudents(); }, 700);
    return () => clearTimeout(timer);
  }, [isVisible, expirationType, fixedExpirationDate, selectedProduct?.id]);

  const handleToggleMigrationUser = (userId) => {
    setMigrationSelectedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleToggleAllMigrationUsers = () => {
    const allIds = (migrationUsers || []).map((user) => user.userId);
    // Comparar por CONTEÚDO, não por tamanho: desmarcar um aluno e marcar outro
    // mantém a contagem e faria o "selecionar todos" limpar a seleção.
    setMigrationSelectedIds((prev) =>
      allIds.length > 0 && allIds.every((id) => prev.includes(id)) ? [] : allIds
    );
  };

  const handleApplyMigration = async () => {
    const productId = getProductAccessId(selectedProduct);
    if (!productId || migrationSelectedIds.length === 0) return;
    // O botão já fica desabilitado, mas o onPress é debounced: uma corrida com o
    // "Salvar" não pode gravar em cima de uma data que não está no produto.
    if (!canApplyMigration) return;

    const losingAccess = (migrationUsers || []).filter(
      (user) =>
        migrationSelectedIds.includes(user.userId) &&
        user.grants?.some((grant) => grant.change === 'shorten' || grant.change === 'limit')
    ).length;

    const confirmed = window.confirm(
      `Alterar a data de fim de acesso de ${migrationSelectedIds.length} aluno(s) para ` +
      `${formatAccessDate(migrationTargetDate)}?` +
      (losingAccess > 0
        ? `\n\nAtenção: ${losingAccess} aluno(s) terão o acesso ENCURTADO.`
        : '')
    );
    if (!confirmed) return;

    setIsApplyingMigration(true);
    setMigrationFeedback("");
    setMigrationError("");

    try {
      const result = await applyProductExpirationMigration(
        productId,
        migrationSelectedIds,
        fixedExpirationDate
      );
      const updated = result?.updatedCount || 0;
      const failed = result?.failedUserIds?.length || 0;

      setMigrationFeedback(
        failed === 0
          ? `Data de fim de acesso atualizada para ${updated} aluno(s).`
          : `Data atualizada para ${updated} aluno(s). Falha em ${failed} aluno(s).`
      );

      // Recarrega para a lista refletir o que ainda está divergente.
      await handleLoadAffectedStudents({ silent: true, force: true });
    } catch (error) {
      console.error('Erro ao aplicar data fixa aos alunos:', error);
      setMigrationError(
        describeMigrationError(
          error,
          'Não foi possível aplicar a data aos alunos. Tente novamente.'
        )
      );
    } finally {
      setIsApplyingMigration(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoadingSave(true);
      setVitrineFeedback(null);

      if (vitrineRef.current?.isBusy?.()) {
        setVitrineFeedback({
          variant: "error",
          message: "Aguarde o envio da imagem da vitrine terminar antes de salvar.",
        });
        return;
      }

      if (vitrineRef.current?.saveVitrine) {
        const vitrineResult = await vitrineRef.current.saveVitrine();
        if (!vitrineResult?.ok) {
          setVitrineFeedback(vitrineResult);
          return;
        }
      }

      const productId = getProductAccessId(selectedProduct);

      if (!productId) {
        return;
      }

      // Precisa ser lido ANTES de atualizar o estado "salvo": é o que decide se,
      // ao terminar de salvar, oferecemos a aplicação retroativa aos alunos.
      const switchedToFixedDate =
        expirationType === "fixedDate" &&
        (savedExpirationType !== "fixedDate" ||
          savedFixedExpirationDate !== fixedExpirationDate);

      // ---- 1) Salvar informações do produto ----
      if (selectedProduct?.marketplace === "Guru Digital") {
        if (expirationType === "fixedDate") {
          const fixedDateError = fixedExpirationDateValidator(fixedExpirationDate);
          if (fixedDateError) {
            setFixedExpirationDateError(fixedDateError);
            return;
          }
          setFixedExpirationDateError("");
        } else if (
          (isProductOrEticket(productInfo?.type) || isProductOrEticket(productType)) &&
          !archived &&
          (!expirationDays || Number.isNaN(expirationDays))
        ) {
          setShowExpirationDaysWarning(true);
          return;
        }

        await onEdit(
          productInfo,
          offersInfos,
          hasExpirationDate,
          expirationDays,
          checkoutUrl,
          archived,
          description,
          selectedProduct,
          expirationType,
          fixedExpirationDate
        );
      }

      // ---- 2) Atualizar relação Produto → Categorias/Cards ----

      // 2.1 Lista de TODOS os cards selecionados
      const selectedCards = [];
      tree.forEach(cat => {
        cat.cards.forEach(card => {
          if (card.selected) {
            selectedCards.push({
              categoryId: cat.id,
              cardId: card.id
            });
          }
        });
      });

      // 2.2 Percorrer TODAS categorias e cards para aplicar add/remove productId
      for (let cat of tree) {
        const cardsSnap = await getDocs(
          collection(firestore, "categories", cat.id, "cards")
        );

        for (let cardDoc of cardsSnap.docs) {
          const cardId = cardDoc.id;
          const categoryId = cat.id;

          const ref = doc(
            firestore,
            "categories",
            categoryId,
            "cards",
            cardId
          );

          const cardIsSelected = selectedCards.some(
            (c) => c.cardId === cardId && c.categoryId === categoryId
          );

          const cardData = cardDoc.data();
          const productIds = cardData?.productIds || [];
          let nextProductIds = [...productIds];

          // Se deve estar selecionado mas não está no Firestore → ADD
          if (cardIsSelected && !productIds.includes(productId)) {
            await updateDoc(ref, {
              productIds: arrayUnion(productId)
            });
            nextProductIds.push(productId);
          }

          // Se NÃO está selecionado mas está no Firestore → REMOVE
          if (!cardIsSelected && productIds.includes(productId)) {
            await updateDoc(ref, {
              productIds: arrayRemove(productId)
            });
            nextProductIds = nextProductIds.filter(id => id !== productId);
          }

          //para nao ficar array de productIds: [] vazio no banco e bloquear card com checkout Pagarme
          if (nextProductIds.length === 0) {
            await updateDoc(ref, {
              productIds: deleteField(),
            });
          }
        }
      }

      // ----------------------------------------------------------

      setShowSuccessMessage(true);

      // Acabou de virar (ou mudar) a data fixa: o modal FICA ABERTO e já lista
      // quem comprou antes da mudança — é a decisão que o admin precisa tomar
      // logo em seguida, e ela nunca acontece sozinha.
      if (switchedToFixedDate && selectedProduct?.marketplace === "Guru Digital") {
        setSavedExpirationType("fixedDate");
        setSavedFixedExpirationDate(fixedExpirationDate);
        await handleLoadAffectedStudents({ force: true });
        return;
      }

      onClose();
    } catch (error) {
      console.error("Erro ao salvar produto:", error);
    } finally {
      setLoadingSave(false);
    }
  }

  const resolvedMarketplaceName =
    productInfo?.marketplace_name ||
    selectedProduct?.marketplaceName ||
    '';

  const isHotmartPlan =
    (productType === 'plan' || selectedProduct?.type === 'plan') &&
    isHotmartMarketplace(resolvedMarketplaceName);

  return (
    <Modal animationType="fade" visible={isVisible} transparent={true}>
      {selectedProduct?.marketplace === "Guru Digital" ? (
        <ModalBackground>
          <ModalContainer>
            <Header>
              <Text style={{ fontFamily: ADMIN_FONT, fontSize: 18, fontWeight: '700', color: FORM.ink }}>
                Editar produto
              </Text>
              <CloseButton onPress={onClose}>
                <Text style={{ fontFamily: ADMIN_FONT, fontSize: 18, color: FORM.muted }}>✕</Text>
              </CloseButton>
            </Header>

            <Content>
              <View style={{ paddingTop: 16, paddingBottom: 4 }}>
                <Text style={{ fontFamily: ADMIN_FONT, fontSize: 20, fontWeight: '700', color: FORM.ink }}>
                  {selectedProduct?.name || productInfo?.name || 'Produto'}
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <Badge variant={(productType === 'plan' || selectedProduct?.type === 'plan') ? 'subscription' : 'product'}>
                    {(productType === 'plan' || selectedProduct?.type === 'plan') ? 'Assinatura' : 'Produto'}
                  </Badge>
                  <Text style={{ fontFamily: ADMIN_FONT, fontSize: 12, color: FORM.muted }}>
                    Código: {selectedProduct?.productId}
                  </Text>
                </View>
              </View>

              {loadingSearch && (
                <ActivityIndicator
                  style={{
                    flex: 1,
                    backgroundColor: "#FFFFFF",
                    justifyContent: "center",
                    paddingVertical: "1rem",
                  }}
                  color="#2e2e2e"
                  size="large"
                />
              )}

              {isVisible && (
                <>
                  <SectionCard title="Detalhes do produto">
                    <Field>
                      <FieldLabel>Descrição</FieldLabel>
                      <AdminInput
                        placeholder="Descrição"
                        value={description}
                        onChangeText={(desc) => setDescription(desc)}
                      />
                    </Field>

                    <Field>
                      <FieldLabel>Checkout URL</FieldLabel>
                      <AdminInput
                        placeholder="Checkout URL"
                        value={checkoutUrl}
                        onChangeText={(url) => setCheckoutUrl(url)}
                      />
                    </Field>

                    <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <View style={{ flex: 1 }}>
                        <FieldLabel>Habilitar no aplicativo</FieldLabel>
                        <Hint>Quando ativo, o produto aparece para os alunos no app.</Hint>
                      </View>
                      <AdminSwitch
                        value={!archived}
                        onValueChange={(value) => setArchived(!value)}
                      />
                    </View>
                  </SectionCard>

                  <CatalogVitrineFields
                    ref={vitrineRef}
                    collectionName="products"
                    docId={selectedProduct?.id}
                    name={selectedProduct?.name || productInfo?.name}
                    accessIds={
                      getProductAccessId(selectedProduct)
                        ? [getProductAccessId(selectedProduct)]
                        : []
                    }
                  />

                  <ProductCatalogFilters
                    collectionName="products"
                    docId={selectedProduct?.id}
                    productName={selectedProduct?.name || productInfo?.name}
                    accessIds={
                      getProductAccessId(selectedProduct)
                        ? [getProductAccessId(selectedProduct)]
                        : []
                    }
                  />

                  {(isProductOrEticket(productType) || (productType === "plan" && !isHotmartPlan)) && (
                    <SectionCard
                      title="Validade do acesso"
                      subtitle="Defina até quando o aluno terá acesso a este produto."
                    >
                      {isProductOrEticket(productType) && !(
                        expirationType === "fixedDate"
                          ? !!fixedExpirationDate
                          : (Number.isFinite(Number(expirationDays)) && Number(expirationDays) >= 1)
                      ) && (
                        <View style={{ marginBottom: 16 }}>
                          <InfoBanner variant="warn">
                            Este é um produto de venda única: preencha a validade (dias após a compra ou data fixa).
                            Sem validade, a compra não libera o conteúdo e os alunos ficam com os cadeados.
                          </InfoBanner>
                        </View>
                      )}
                      <Field style={{ marginBottom: 16 }}>
                        <FieldLabel>Modo de validade</FieldLabel>
                        <SegmentedControl
                          value={expirationType}
                          onChange={(value) => setExpirationType(value)}
                          accentColor={FORM.teal}
                          options={[
                            { value: "days", label: "Dias após a compra", description: "Cada aluno perde o acesso N dias após a compra.", icon: "time-outline" },
                            { value: "fixedDate", label: "Data fixa de fim de acesso", description: "Todos os alunos perdem o acesso na mesma data.", icon: "calendar-outline" },
                          ]}
                        />
                      </Field>

                      {expirationType === "fixedDate" && (
                        <Field style={{ marginBottom: 0 }}>
                          <FieldLabel>Data fixa de fim de acesso</FieldLabel>
                          <AdminDatePicker
                            value={fixedExpirationDate}
                            onChange={(iso) => {
                              setFixedExpirationDate(iso);
                              setFixedExpirationDateError("");
                            }}
                          />
                          <InfoBanner variant="info">
                            Esta data de fim de acesso é a mesma para todos os alunos, independente de quando compraram.
                          </InfoBanner>
                          <FieldError>{fixedExpirationDateError}</FieldError>
                        </Field>
                      )}

                      {expirationType === "days" && isProductOrEticket(productType) && (
                        <Field style={{ marginBottom: 0 }}>
                          <FieldLabel>Dias de validade após a compra</FieldLabel>
                          <AdminInput
                            placeholder="Ex.: 365"
                            value={Number.isFinite(Number(expirationDays)) && expirationDays !== "" ? String(expirationDays) : ""}
                            onChangeText={(days) => {
                              const digits = (days || "").replace(/\D/g, "");
                              setExpirationDays(digits === "" ? "" : parseInt(digits, 10));
                            }}
                            keyboardType="numeric"
                          />
                          {showExpirationDaysWarning && (
                            <FieldError>Por favor, preencha os dias de expiração para salvar as alterações.</FieldError>
                          )}
                        </Field>
                      )}

                      {expirationType === "days" && productType === "plan" && (
                        <Field style={{ marginBottom: 0 }}>
                          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                            <View style={{ flex: 1 }}>
                              <FieldLabel>Definir validade em dias</FieldLabel>
                              <Hint>Ative apenas se este produto for uma venda única (one-time purchase).</Hint>
                            </View>
                            <AdminSwitch
                              value={hasExpirationDate}
                              onValueChange={(value) => setHasExpirationDate(value)}
                              accent={FORM.accent}
                            />
                          </View>
                          {hasExpirationDate && (
                            <View style={{ marginTop: 12 }}>
                              <AdminInput
                                placeholder="Dias de Expiração"
                                value={Number.isFinite(Number(expirationDays)) && expirationDays !== "" ? String(expirationDays) : ""}
                                onChangeText={(days) => {
                                  const digits = (days || "").replace(/\D/g, "");
                                  setExpirationDays(digits === "" ? "" : parseInt(digits, 10));
                                }}
                                keyboardType="numeric"
                              />
                            </View>
                          )}
                        </Field>
                      )}
                    </SectionCard>
                  )}

                  {/* Aplicação RETROATIVA: quem comprou antes da mudança segue
                      com a data antiga. Nada é alterado sem o admin selecionar. */}
                  {(isProductOrEticket(productType) || (productType === "plan" && !isHotmartPlan)) &&
                    expirationType === "fixedDate" && (
                    <SectionCard
                      title="Alunos que já têm este produto"
                      subtitle="Quem comprou ou recebeu acesso manual antes desta mudança continua com a data antiga. Aqui você iguala todos à data fixa."
                    >
                      {fixedExpirationDateValidator(fixedExpirationDate) !== "" ? (
                        <InfoBanner variant="info">
                          Escolha a data fixa acima para ver quem já tem este produto e com
                          qual data cada um está hoje.
                        </InfoBanner>
                      ) : (
                        <>
                          <Button
                            title={isLoadingMigration ? "Consultando alunos..." : "Atualizar lista"}
                            onPress={() => handleLoadAffectedStudents({ force: true })}
                            isLoading={isLoadingMigration}
                            disabled={isLoadingMigration || isApplyingMigration}
                            colorbutton={FORM.secondaryBg}
                            colortitle={FORM.secondaryText}
                            width="100%"
                          />

                          {!!migrationUsers?.length && (
                            <>
                              <View style={{ marginTop: 16, marginBottom: 8 }}>
                                <AdminCheckbox
                                  value={
                                    migrationUsers.length > 0 &&
                                    migrationUsers.every((user) =>
                                      migrationSelectedIds.includes(user.userId)
                                    )
                                  }
                                  onValueChange={handleToggleAllMigrationUsers}
                                  label={`Selecionar todos (${migrationUsers.length} aluno(s) com data diferente)`}
                                />
                              </View>

                              <ScrollView style={{ maxHeight: windowHeight * 0.32 }} nestedScrollEnabled>
                                {migrationUsers.map((user) => {
                                  const grant = user.grants?.[0];
                                  if (!grant) return null;
                                  const description = describeGrantChange(grant, migrationTargetDate);
                                  const statusBadge = GRANT_STATUS_BADGE[grant.status];
                                  const toneColor =
                                    description.tone === 'danger'
                                      ? FORM.danger
                                      : description.tone === 'warn'
                                        ? FORM.warnText
                                        : FORM.label;

                                  return (
                                    <View
                                      key={user.userId}
                                      style={{
                                        flexDirection: 'row',
                                        alignItems: 'flex-start',
                                        gap: 10,
                                        paddingVertical: 8,
                                        borderBottomWidth: 1,
                                        borderBottomColor: FORM.border,
                                      }}
                                    >
                                      <AdminCheckbox
                                        value={migrationSelectedIds.includes(user.userId)}
                                        onValueChange={() => handleToggleMigrationUser(user.userId)}
                                        style={{ marginTop: 2 }}
                                      />
                                      <View style={{ flex: 1 }}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                          <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, fontWeight: '600', color: FORM.ink }}>
                                            {user.email || user.name || user.userId}
                                          </Text>
                                          <Badge variant="neutral">{grant.isManual ? 'Manual' : 'Guru'}</Badge>
                                          {statusBadge && (
                                            <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                                          )}
                                          {user.grants.length > 1 && (
                                            <Badge variant="neutral">{`${user.grants.length} registros`}</Badge>
                                          )}
                                        </View>
                                        <Text style={{ fontFamily: ADMIN_FONT, fontSize: 12, color: toneColor, marginTop: 2 }}>
                                          {description.text}
                                        </Text>
                                      </View>
                                    </View>
                                  );
                                })}
                              </ScrollView>

                              <View style={{ marginTop: 12 }}>
                                <InfoBanner variant="warn">
                                  Alunos com acesso expirado e vitalício vêm desmarcados: aplicar a data
                                  neles REATIVA o acesso (expirados) ou passa a limitá-lo (vitalícios).
                                </InfoBanner>
                              </View>

                              {!canApplyMigration && (
                                <View style={{ marginTop: 12 }}>
                                  <InfoBanner variant="info">
                                    Esta é uma simulação da data escolhida. Salve as alterações do
                                    produto para poder aplicá-la aos alunos.
                                  </InfoBanner>
                                </View>
                              )}

                              <View style={{ marginTop: 12 }}>
                                <Button
                                  title={`Aplicar ${formatAccessDate(migrationTargetDate)} a ${migrationSelectedIds.length} aluno(s)`}
                                  onPress={handleApplyMigration}
                                  isLoading={isApplyingMigration}
                                  disabled={
                                    isApplyingMigration ||
                                    migrationSelectedIds.length === 0 ||
                                    !canApplyMigration
                                  }
                                  colorbutton={canApplyMigration ? FORM.teal : FORM.secondaryBg}
                                  colortitle={canApplyMigration ? "#ffffff" : FORM.muted}
                                  width="100%"
                                />
                              </View>
                            </>
                          )}

                          {!!migrationSummary?.truncated && (
                            <View style={{ marginTop: 12 }}>
                              <InfoBanner variant="warn">
                                Muitos alunos afetados: a lista mostra os primeiros 1000. Aplique nestes
                                e clique em "Atualizar lista" para continuar.
                              </InfoBanner>
                            </View>
                          )}

                          {migrationFeedback !== "" && (
                            <View style={{ marginTop: 12 }}>
                              <InfoBanner variant="success">{migrationFeedback}</InfoBanner>
                            </View>
                          )}

                          {migrationError !== "" && (
                            <View style={{ marginTop: 12 }}>
                              <InfoBanner variant="error">{migrationError}</InfoBanner>
                            </View>
                          )}
                        </>
                      )}
                    </SectionCard>
                  )}

                  {productType === "plan" && Array.isArray(offersInfos?.data) && (
                    <ViewTextInput>
                      <View style={{ marginTop: 8, marginBottom: 4 }}>
                        <Text style={{ fontFamily: ADMIN_FONT, fontSize: 16, fontWeight: '700', color: FORM.ink }}>
                          Ofertas e assinaturas
                        </Text>
                        <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, color: FORM.muted, marginTop: 2 }}>
                          Gerencie as ofertas deste produto e migre assinaturas entre ofertas.
                        </Text>
                      </View>

                      {/* Lista de ofertas enquanto nenhuma foi escolhida */}
                      {!isRenewalSectionOpen && (
                        <ScrollView
                          style={{
                            maxHeight: windowHeight * 0.35,
                            marginTop: "0.5rem",
                          }}
                        >
                          {offersInfos.data.map((offer) => (
                            <View
                              key={offer.id}
                              style={{
                                borderWidth: 1,
                                borderColor: FORM.border,
                                borderRadius: 10,
                                padding: 14,
                                marginBottom: 10,
                                backgroundColor: FORM.surface,
                              }}
                            >
                              <Text style={{ fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600', color: FORM.ink }}>
                                {offer.name || offer.title || offer.id}
                              </Text>
                              <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, color: FORM.label, marginTop: 4 }}>
                                Valor: {offer.price_format || offer.price || offer.amount || offer.value || "não informado"}
                              </Text>
                              <Text style={{ fontFamily: ADMIN_FONT, fontSize: 11, color: FORM.muted, marginTop: 2 }}>
                                ID: {offer.id}
                              </Text>
                              <TouchableOpacity
                                onPress={async () => {
                                  handleToggleRenewalSection(offer.id);
                                  await handleLoadSubscriptionsForOffer(offer.id);
                                }}
                                disabled={
                                  loadingSubscriptions &&
                                  selectedOfferForRenewalId === offer.id
                                }
                                style={{
                                  marginTop: 10,
                                  alignSelf: "flex-start",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: 6,
                                  paddingVertical: 5,
                                  paddingHorizontal: 10,
                                  borderRadius: 8,
                                  borderWidth: 1,
                                  borderColor: FORM.border,
                                  backgroundColor: FORM.surfaceSubtle,
                                }}
                              >
                                <Octicons name="sync" size={12} color={FORM.muted} />
                                <Text style={{ fontFamily: ADMIN_FONT, fontSize: 12, fontWeight: "600", color: FORM.muted }}>
                                  {loadingSubscriptions &&
                                  selectedOfferForRenewalId === offer.id
                                    ? "Carregando..."
                                    : "Alterar assinaturas"}
                                </Text>
                              </TouchableOpacity>
                            </View>
                          ))}
                        </ScrollView>
                      )}

                      {/* Seção de alteração quando uma oferta foi escolhida */}
                      {isRenewalSectionOpen && selectedOfferForRenewalId && (
                        <>
                          <TouchableOpacity
                            onPress={() => handleToggleRenewalSection(null)}
                            style={{ flexDirection: "row", alignItems: "center", gap: 6, marginTop: 8, marginBottom: 4 }}
                          >
                            <Octicons name="chevron-left" size={18} color={FORM.muted} />
                            <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, color: FORM.muted }}>
                              Voltar para as ofertas
                            </Text>
                          </TouchableOpacity>

                          <Field>
                            <FieldLabel>ID da nova oferta de renovação</FieldLabel>
                            <AdminInput
                              placeholder="ID da nova oferta"
                              value={newRenewalOfferId}
                              onChangeText={setNewRenewalOfferId}
                            />
                          </Field>

                          {subscriptions.length > 0 && (
                            <>
                              <ViewTextInput>
                                <ViewRow>
                                  <CheckBox
                                    title="Selecionar todas as assinaturas desta oferta"
                                    checked={
                                      selectedSubscriptionIds.length ===
                                        subscriptions.length &&
                                      subscriptions.length > 0
                                    }
                                    onPress={handleToggleSelectAllSubscriptions}
                                    containerStyle={{
                                      backgroundColor: "transparent",
                                      paddingVertical: 0,
                                    }}
                                  />
                                </ViewRow>

                                <ScrollView
                                  style={{
                                    maxHeight: windowHeight * 0.3,
                                    marginTop: "0.5rem",
                                  }}
                                >
                                  {subscriptions.map((sub) => (
                                    <CheckBox
                                      key={sub.id}
                                      title={`E-mail: ${
                                        sub.userEmail || sub.userId
                                      } | Assinatura: ${sub.id} | Status: ${
                                        sub.status
                                      }`}
                                      checked={selectedSubscriptionIds.includes(
                                        sub.id
                                      )}
                                      onPress={() =>
                                        handleToggleSubscriptionSelection(sub.id)
                                      }
                                      containerStyle={{
                                        backgroundColor: "transparent",
                                        paddingVertical: 0,
                                      }}
                                      textStyle={{ fontSize: 12 }}
                                    />
                                  ))}
                                </ScrollView>
                              </ViewTextInput>

                              <ViewTextInput>
                                <Button
                                  title="Aplicar nova oferta de renovação"
                                  onPress={handleApplyRenewalOfferToSelected}
                                  isLoading={isUpdatingRenewalOffer}
                                  disabled={isUpdatingRenewalOffer}
                                  colorbutton={FORM.accent}
                                  colortitle={"#ffffff"}
                                  width="100%"
                                />
                              </ViewTextInput>
                            </>
                          )}

                          {renewalFeedbackMessage !== "" && (
                            <ViewTextInput>
                              <FooterText color="#2e2e2e" padding="0.5rem 0rem">
                                {renewalFeedbackMessage}
                              </FooterText>
                              {renewalFailedEmails.length > 0 && (
                                <>
                                  <SmallText color="#2e2e2e" padding="0.25rem 0rem">
                                    E-mails com falha:
                                  </SmallText>
                                  <ScrollView
                                    style={{
                                      maxHeight: windowHeight * 0.2,
                                      marginTop: 4,
                                    }}
                                    nestedScrollEnabled
                                  >
                                    {renewalFailedEmails.map((email) => (
                                      <FooterText
                                        key={email}
                                        color="#2e2e2e"
                                        padding="0.15rem 0rem"
                                      >
                                        • {email}
                                      </FooterText>
                                    ))}
                                  </ScrollView>
                                </>
                              )}
                            </ViewTextInput>
                          )}
                        </>
                      )}
                    </ViewTextInput>
                  )}

                  {isHotmartPlan && (
                    <SectionCard
                      title="Assinaturas atrasadas na Hotmart"
                      subtitle="Assinantes atrasados há 5 dias ou mais, prontos para cancelamento."
                    >
                      <AdminButton
                        title="Listar assinaturas atrasadas na Hotmart"
                        onPress={handleListHotmartDelayedSubscriptions}
                        isLoading={isLoadingHotmartDelayedSubscriptions}
                        variant="secondary"
                        icon="list-outline"
                        fullWidth
                      />

                      {hotmartDelayedFeedbackMessage !== '' && (
                        <View style={{ marginTop: 12 }}>
                          <InfoBanner variant={getHotmartFeedbackVariant(hotmartDelayedFeedbackMessage)}>
                            {hotmartDelayedFeedbackMessage}
                          </InfoBanner>
                        </View>
                      )}

                      {hotmartDelayedSubscriptions.length > 0 && (
                        <>
                          <View style={{ marginTop: 16, marginBottom: 8 }}>
                            <AdminCheckbox
                              value={
                                selectedHotmartSubscriptionCodes.length ===
                                  hotmartDelayedSubscriptions
                                    .map(getHotmartSubscriptionSelectionId)
                                    .filter(Boolean).length &&
                                hotmartDelayedSubscriptions.length > 0
                              }
                              onValueChange={handleToggleSelectAllHotmartSubscriptions}
                              label={`Selecionar todas (${hotmartDelayedSubscriptions.length} assinatura(s) atrasada(s))`}
                            />
                          </View>

                          <ScrollView
                            style={{ maxHeight: windowHeight * 0.3 }}
                            nestedScrollEnabled
                          >
                            {hotmartDelayedSubscriptions.map((subscription) => {
                              const selectionId = getHotmartSubscriptionSelectionId(subscription);

                              return (
                                <View
                                  key={selectionId}
                                  style={{
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    gap: 10,
                                    paddingVertical: 8,
                                    borderBottomWidth: 1,
                                    borderBottomColor: FORM.border,
                                  }}
                                >
                                  <AdminCheckbox
                                    value={selectedHotmartSubscriptionCodes.includes(selectionId)}
                                    onValueChange={() => handleToggleHotmartSubscriptionSelection(selectionId)}
                                    style={{ marginTop: 2 }}
                                  />
                                  <View style={{ flex: 1 }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                      <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, fontWeight: '600', color: FORM.ink }}>
                                        {subscription.email || 'E-mail não informado'}
                                      </Text>
                                      <Badge variant="danger">{`${subscription.daysDelayed} dia(s) em atraso`}</Badge>
                                    </View>
                                    {subscription.subscriberCode ? (
                                      <Text style={{ fontFamily: ADMIN_FONT, fontSize: 11, color: FORM.muted, marginTop: 2 }}>
                                        Código: {subscription.subscriberCode}
                                      </Text>
                                    ) : null}
                                  </View>
                                </View>
                              );
                            })}
                          </ScrollView>

                          <Text style={{ fontFamily: ADMIN_FONT, fontSize: 12, color: FORM.muted, marginTop: 10 }}>
                            Selecionadas: {selectedHotmartSubscriptionCodes.length} de{' '}
                            {hotmartDelayedSubscriptions.length}
                          </Text>

                          <View style={{ marginTop: 12 }}>
                            <AdminButton
                              title="Cancelar assinaturas selecionadas na Hotmart"
                              onPress={handleCancelHotmartSubscriptions}
                              isLoading={isCancellingHotmartSubscriptions}
                              disabled={
                                isCancellingHotmartSubscriptions ||
                                selectedHotmartSubscriptionCodes.length === 0
                              }
                              variant="dangerGhost"
                              icon="close-circle-outline"
                              fullWidth
                            />
                          </View>
                        </>
                      )}
                    </SectionCard>
                  )}

                  {loadingTree ? (
                    <ActivityIndicator size="small" />
                  ) : (
                  <View
                    style={{
                      marginTop: 16,
                      maxHeight: windowHeight * 0.4,
                      borderWidth: 1,
                      borderColor: FORM.border,
                      borderRadius: 12,
                      backgroundColor: FORM.surface,
                      padding: 16,
                    }}
                  >
                    <Text style={{ fontFamily: ADMIN_FONT, fontSize: 16, fontWeight: '700', color: FORM.ink }}>
                      Vincular a categorias ou módulos
                    </Text>
                    <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, color: FORM.muted, marginTop: 2, marginBottom: 10 }}>
                      Selecione onde este produto libera acesso ao aluno.
                    </Text>
                    <ScrollView>
                      {tree.map(cat => {
                        const isExpanded = expandedCategoryIds?.includes(cat.id);
                        return (
                          <View key={cat.id}>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                              <TouchableOpacity onPress={() => {
                                setExpandedCategoryIds(prev =>
                                  prev?.includes(cat.id)
                                    ? prev.filter(id => id !== cat.id)
                                    : [...(prev || []), cat.id]
                                );
                              }}>
                                <Octicons
                                  name={isExpanded ? 'chevron-down' : 'chevron-right'}
                                  size={18}
                                  color="#333"
                                  style={{ marginRight: 6 }}
                                />
                              </TouchableOpacity>
                              <CheckBox
                                title={"Categoria: " + cat.title}
                                checked={cat.selected}
                                onPress={() => {
                                  setTree(t =>
                                    t.map(c =>
                                      c.id === cat.id
                                        ? {
                                          ...c,
                                          selected: !c.selected,
                                          cards: c.cards.map(cd => ({
                                            ...cd,
                                            selected: !c.selected
                                          }))
                                        }
                                        : c
                                    )
                                  );
                                }}
                                containerStyle={{ backgroundColor: 'transparent', paddingVertical: 0, flex: 1, borderWidth: 0, marginLeft: 0 }}
                                checkedColor={FORM.accent}
                                uncheckedColor={FORM.muted}
                                textStyle={{ fontFamily: ADMIN_FONT, color: FORM.label, fontSize: 14, fontWeight: '600' }}
                              />
                            </View>

                            {isExpanded && cat.cards.map(cd => (
                              <CheckBox
                                key={cd.id}
                                title={"Módulo:" + cd.title}
                                checked={cd.selected}
                                onPress={() => {
                                  setTree(t =>
                                    t.map(c =>
                                      c.id === cat.id
                                        ? {
                                          ...c,
                                          cards: c.cards.map(cc =>
                                            cc.id === cd.id ? { ...cc, selected: !cc.selected } : cc
                                          ),
                                        }
                                        : c
                                    )
                                  );
                                }}
                                containerStyle={{ backgroundColor: "transparent", paddingVertical: 0, paddingLeft: 24, borderWidth: 0, marginLeft: 0 }}
                                checkedColor={FORM.accent}
                                uncheckedColor={FORM.muted}
                                textStyle={{ fontSize: 14, fontFamily: ADMIN_FONT, color: FORM.muted }}
                              />
                            ))}
                          </View>
                        );
                      })}
                    </ScrollView>
                  </View>
                  )}

                  {/* Secundário à esquerda, principal à direita — mesma ordem do
                      rodapé dos outros modais do painel. */}
                  {vitrineFeedback && (
                    <View style={{ marginTop: 16, marginBottom: 4 }}>
                      <AdminAlert variant={vitrineFeedback.variant}>
                        {vitrineFeedback.message}
                      </AdminAlert>
                    </View>
                  )}
                  <View style={{ flexDirection: "row", gap: 12, marginTop: 20, marginBottom: 8 }}>
                    <View style={{ flex: 1 }}>
                      <AdminButton
                        title="Cancelar"
                        onPress={onClose}
                        variant="secondary"
                        fullWidth
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <AdminButton
                        title="Salvar alterações"
                        isLoading={loadingSave}
                        onPress={handleEdit}
                        variant="primary"
                        fullWidth
                      />
                    </View>
                  </View>

                  {onDelete && (
                    <View style={{ marginTop: 4, marginBottom: 8 }}>
                      <AdminButton
                        title="Excluir produto"
                        isLoading={isDeleting}
                        onPress={() => onDelete(selectedProduct)}
                        variant="dangerGhost"
                        icon="trash-outline"
                        fullWidth
                      />
                      <Text style={{ fontFamily: ADMIN_FONT, fontSize: 12, color: FORM.muted, marginTop: 6, textAlign: 'center' }}>
                        Só é possível excluir um produto sem nenhum aluno com acesso ativo.
                      </Text>
                    </View>
                  )}
                </>
              )}

              {guruErrorMessage !== '' && (
                <View style={{ paddingVertical: 12 }}>
                  <InfoBanner variant="error">{guruErrorMessage}</InfoBanner>
                </View>
              )}

              {showSuccessMessage && (
                <View style={{ paddingVertical: 12 }}>
                  <InfoBanner variant="success">Alterações salvas com sucesso!</InfoBanner>
                </View>
              )}

            </Content>
          </ModalContainer>
        </ModalBackground>
      ) : (
        <ModalBackground>
          <GenericProduct
            productData={selectedProduct}
            onClose={onClose}
            tree={tree}
            setTree={setTree}
            expandedCategoryIds={expandedCategoryIds}
            setExpandedCategoryIds={setExpandedCategoryIds}
            loadingSave={loadingSave}
            handleEdit={handleEdit}
            onDelete={onDelete}
            isDeleting={isDeleting}
            vitrineRef={vitrineRef}
            vitrineFeedback={vitrineFeedback}
          />
        </ModalBackground>
      )}
    </Modal>
  );
};

export default EditProductModal;